import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@fantaz/shared/app-config';
import {
  ConfirmPromoCodeResponse,
  PromoCode,
  PromoCodesResponse,
} from '@fantaz/shared/data-access/models';
import { catchError, map, Observable, of, throwError } from 'rxjs';

export const PROMO_CODE_LOCAL_STORAGE_KEY = 'promo-code';
@Injectable({
  providedIn: 'root',
})
export class PromoService {
  constructor(private http: HttpClient) {}

  /**
   * Function to store promo code into local-storage a one promo code
   *
   * @param promoCode string
   */
  saveInLocalStorage(promoCode: string): Observable<string> {
    if (!promoCode) {
      throw new Error('Promo code is required');
    }

    if (typeof promoCode !== 'string') {
      throw new Error('Promo code must be a string');
    }

    if (!localStorage) {
      throw new Error('Local storage is not supported');
    }

    // store promo code into local storage and return it
    localStorage.setItem(
      PROMO_CODE_LOCAL_STORAGE_KEY,
      JSON.stringify({ value: promoCode, used: false }),
    );

    return of(promoCode);
  }

  /**
   * Load from local storage a one promo code
   */
  loadFromLocalStorage(options?: {
    markSuchAsUsed?: boolean;
  }): { value: string; used: boolean } | null | undefined {
    if (!localStorage) {
      throw new Error('Local storage is not supported');
    }

    // load promo code from local storage and convert it to object
    const promoCode = localStorage.getItem(PROMO_CODE_LOCAL_STORAGE_KEY);

    if (!promoCode) {
      return null;
    }

    let obj;

    try {
      obj = JSON.parse(promoCode);
    } catch {
      obj = {
        value: promoCode,
        used: false,
      };
    }

    if (options?.markSuchAsUsed) {
      localStorage.setItem(
        PROMO_CODE_LOCAL_STORAGE_KEY,
        JSON.stringify({
          value: obj.value,
          used: true,
        }),
      );
    }

    return obj;
  }

  loadFromLocalStorage$() {
    return localStorage.getItem(PROMO_CODE_LOCAL_STORAGE_KEY);
  }

  /**
   * Function to remove from local storage a one promo code
   *
   * @param promoCode string
   */
  removeFromLocalStorage(options?: { force?: boolean }): Observable<void> {
    this.removePromoCodeFromLocalStorage(options);
    return of();
  }

  removePromoCodeFromLocalStorage(options?: { force?: boolean }): void {
    const data = this.loadFromLocalStorage();

    if (data) {
      localStorage.setItem(
        PROMO_CODE_LOCAL_STORAGE_KEY,
        JSON.stringify({
          value: data.value,
          used: true,
        }),
      );
    }

    if (options?.force) {
      localStorage.removeItem(PROMO_CODE_LOCAL_STORAGE_KEY);
    }
  }

  /**
   * Apply promo code
   *
   * @param promoCode string
   * @returns {Observable<PromoCode>} promise with promo code
   */
  applyPromoCode(promoCode: string): Observable<PromoCode> {
    // prepare endpoint
    const endpoint = `${environment.apiBaseUrl}/core-client/api/promo-code`;

    // call endpoint
    return this.http
      .post<ConfirmPromoCodeResponse>(endpoint, { promoCode })
      .pipe(
        map((resp) => {
          return resp?.item;
        }),
        catchError((err) => {
          if (err && err.error && err.error.message) {
            return throwError(() => ({ message: err.error.message }));
          }
          return throwError(() => err);
        }),
      );
  }

  /**
   * Confirm promo code
   * @deprecated
   * @param promoCode string
   * @returns {Observable<PromoCode>} promise with promo code
   */
  confirmPromoCode(promoCode: string): Observable<PromoCode> {
    // TODO: Update API Url
    const endpoint = `${environment.apiBaseUrl}/core-client/api/promo-code`;
    return this.http
      .post<ConfirmPromoCodeResponse>(endpoint, { promoCode })
      .pipe(
        map((resp) => {
          return resp?.item;
        }),
      );
  }

  loadPromoCodes(params: {
    pageIndex?: number;
    pageSize?: number;
  }): Observable<PromoCodesResponse> {
    return this.http.get<PromoCodesResponse>(
      `${environment.apiBaseUrl}/core-client/api/promo-code`,
      { params },
    );
  }
}
